import React, { useState, useEffect } from "react";

type SafeNumberInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "value" | "onChange" | "type"
> & {
  /** The “committed” numeric value */
  value: number;

  /** Called only with a valid number parse */
  onChange: (value: number) => void;

  /** Optional HTML number attributes */
  min?: number;
  max?: number;
  step?: number;
};

const SafeNumberInput: React.FC<SafeNumberInputProps> = ({
  value,
  onChange,
  min,
  max,
  step,
  ...rest
}) => {
  // internal string to back the <input>
  const [text, setText] = useState<string>(value.toString());

  // keep text in sync when parent value changes
  useEffect(() => {
    setText(value.toString());
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newText = e.target.value;
    setText(newText);

    // only propagate if parseFloat succeeds
    const parsed = parseFloat(newText);
    if (!isNaN(parsed)) {
      onChange(parsed);
    }
  };

  const handleBlur = () => {
    // if they left it blank or invalid, reset to the last valid value
    if (text.trim() === "" || isNaN(parseFloat(text))) {
      setText(value.toString());
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      e.preventDefault();
      const current = parseFloat(text);
      const isValid = !isNaN(current);
      const stepValue = step ?? 0.1;
      let nextValue = isValid ? current : value;
      if (e.key === "ArrowUp") {
        nextValue += stepValue;
        // format to avoid floating point precision issues
        nextValue = parseFloat(nextValue.toFixed(1));
      } else {
        nextValue -= stepValue;
        nextValue = parseFloat(nextValue.toFixed(1));
      }
      if (typeof min === "number") nextValue = Math.max(min, nextValue);
      if (typeof max === "number") nextValue = Math.min(max, nextValue);
      setText(nextValue.toString());
      onChange(nextValue);
    }
  };

  return (
    <input
      type="text"
      inputMode="decimal"
      pattern="[0-9]+([.,][0-9]+)?"
      value={text}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      min={min}
      max={max}
      step={step}
      {...rest}
    />
  );
};

export default SafeNumberInput;
